"use client";

import { AnimatePresence, motion } from "framer-motion";
import { useSearchParams } from "next/navigation";
import { useSession } from "next-auth/react";
import { useLocale } from "next-intl";

import { useCalendar } from "@/app/hooks/useCalendar";
import { CalendarSkeleton } from "@/components/calendar/CalendarSkeleton";
import { CalendarSurface } from "@/components/calendar/CalendarSurface";
import { PageHeader } from "@/components/shared/PageHeader";
import { crossfadeVariants } from "@/lib/animations";
import {
  localToday,
  parseIsoDate,
  startOfWeek,
  toIsoDate,
  weekStartDowForLocale,
} from "@/lib/calendar/week";

export default function CalendarPage() {
  const locale = useLocale();
  const { data: session, status: sessionStatus } = useSession();
  const userId = session?.user?.id ?? null;
  const searchParams = useSearchParams();
  const weekOf = searchParams.get("weekOf") ?? undefined;

  const dow = weekStartDowForLocale(locale);
  const todayIso = localToday();
  const baseDate = parseIsoDate(weekOf) ?? parseIsoDate(todayIso)!;
  const weekStart = startOfWeek(baseDate, dow);
  const weekStartIso = toIsoDate(weekStart);

  const { data, isLoading } = useCalendar(weekOf, locale, !!userId);

  const showSkeleton = sessionStatus === "loading" || (!!userId && isLoading);

  return (
    <AnimatePresence mode="wait">
      {showSkeleton ? (
        <motion.div
          key="skeleton"
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={crossfadeVariants}
        >
          <PageHeader
            rightSlot={
              <div className="flex items-center gap-2">
                <div className="size-9 animate-pulse rounded-md bg-muted" />
                <div className="size-9 animate-pulse rounded-md bg-muted" />
                <div className="size-9 animate-pulse rounded-md bg-muted" />
              </div>
            }
          />
          <CalendarSkeleton />
        </motion.div>
      ) : (
        <motion.div
          key="content"
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={crossfadeVariants}
        >
          <CalendarSurface
            locale={locale}
            weekStartIso={data?.weekStart ?? weekStartIso}
            todayIso={todayIso}
            entries={data?.entries ?? []}
            isGuest={!userId}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
